import { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe2, Smartphone, Sparkles, Loader2, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';
import TemplateEditorial from '@/components/website-templates/TemplateEditorial';
import TemplateBold from '@/components/website-templates/TemplateBold';
import TemplateShowcase from '@/components/website-templates/TemplateShowcase';
import { APP_SCREENS, PhoneFrame } from '@/components/AppShellPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'editorial', label: 'Editorial', Component: TemplateEditorial },
  { id: 'bold', label: 'Bold', Component: TemplateBold },
  { id: 'showcase', label: 'Showcase', Component: TemplateShowcase },
];

const PAGE_OPTIONS = [
  'Home',
  'About',
  'Services',
  'Portfolio/Work',
  'Pricing',
  'Contact',
  'Blog',
  'FAQ',
  'Login/Dashboard',
  'E-commerce Shop',
];

const INTEGRATION_OPTIONS = [
  'Payment (Stripe/PayPal)',
  'Booking System',
  'CRM',
  'Email Marketing',
  'Analytics',
  'Chat Widget',
  'Maps',
  'Social Feeds',
];

const DEVICE_OPTIONS = ['Desktop', 'Mobile', 'Tablet'];

const EMPTY_SITE_BRIEF = {
  pages_required: [],
  additional_pages: '',
  integrations_needed: [],
  has_cms: false,
  has_ecommerce: false,
  has_booking: false,
  target_devices: ['Desktop', 'Mobile', 'Tablet'],
  deadline: '',
  special_requirements: '',
};

const ToggleYesNo = ({ value, onChange, label }) => (
  <div className="space-y-1">
    <Label className="text-foreground/80">{label}</Label>
    <div className="flex gap-2">
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          onClick={() => onChange(v)}
          className={`px-4 py-1.5 text-sm border transition-colors ${
            value === v
              ? 'bg-primary/10 text-primary border-primary/30'
              : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
          }`}
        >
          {v ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  </div>
);

const WebsiteShellPanel = () => {
  const { currentProject, brandKit } = useProject();

  const [content, setContent] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templateId, setTemplateId] = useState('editorial');
  const [viewMode, setViewMode] = useState('web'); // 'web' | 'app'
  const [screenId, setScreenId] = useState('home');

  // BP-02-A: Digital Requirements
  const [siteBriefOpen, setSiteBriefOpen] = useState(false);
  const [siteBrief, setSiteBrief] = useState({ ...EMPTY_SITE_BRIEF });
  const [siteBriefSaving, setSiteBriefSaving] = useState(false);

  const setSiteBriefField = (key, value) => setSiteBrief((prev) => ({ ...prev, [key]: value }));

  const toggleArrayItem = (key, item) => {
    setSiteBrief((prev) => ({
      ...prev,
      [key]: prev[key].includes(item)
        ? prev[key].filter((v) => v !== item)
        : [...prev[key], item],
    }));
  };

  const fetchContent = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/site-content`);
      setContent(res.data);
    } catch (error) {
      console.error('Failed to fetch site content:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchSiteBrief = async () => {
    if (!currentProject) return;
    try {
      const res = await axios.get(`${API}/projects/${currentProject.id}/site-brief`);
      if (res.data) setSiteBrief({ ...EMPTY_SITE_BRIEF, ...res.data });
    } catch {
      // No brief saved yet
    }
  };

  useEffect(() => {
    fetchContent();
    fetchSiteBrief();
  }, [currentProject?.id]);

  const handleSaveSiteBrief = async () => {
    try {
      setSiteBriefSaving(true);
      await axios.post(`${API}/projects/${currentProject.id}/site-brief`, siteBrief);
      toast.success('Digital requirements saved');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to save site brief');
    } finally {
      setSiteBriefSaving(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/projects/${currentProject.id}/site-content/generate`);
      setContent(res.data);
      toast.success('Website copy generated');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate website copy');
    } finally {
      setGenerating(false);
    }
  };

  if (!currentProject) return null;

  const ActiveTemplate = TEMPLATES.find((t) => t.id === templateId)?.Component;
  const domain = (brandKit?.business_name || currentProject.name || 'yourbusiness')
    .toLowerCase().replace(/[^a-z0-9]+/g, '') + '.com';

  return (
    <div className="h-full overflow-auto p-6" data-testid="website-shell-panel">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Website &amp; App Shell</h2>
              <p className="text-muted-foreground">A design presentation, not a real hosted site or app &mdash; pick a direction, the real build happens after sign-off</p>
            </div>
            <ReportButton section="website" projectId={currentProject.id} />
          </div>

          {/* BP-02-A: Digital Requirements (collapsible) */}
          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <button
              type="button"
              onClick={() => setSiteBriefOpen((o) => !o)}
              className="flex items-center gap-2 w-full text-left"
            >
              {siteBriefOpen ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
              <span className="font-mono text-xs uppercase tracking-wide lodge-accent">
                Digital Requirements (BP-02-A)
              </span>
            </button>

            {siteBriefOpen && (
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Pages Required</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {PAGE_OPTIONS.map((p) => (
                      <label key={p} className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={siteBrief.pages_required.includes(p)}
                          onChange={() => toggleArrayItem('pages_required', p)}
                          className="accent-primary"
                        />
                        {p}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-foreground/80">Additional Pages</Label>
                  <input
                    type="text"
                    value={siteBrief.additional_pages}
                    onChange={(e) => setSiteBriefField('additional_pages', e.target.value)}
                    placeholder="Any pages not listed above"
                    className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground/80">Integrations Needed</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {INTEGRATION_OPTIONS.map((i) => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={siteBrief.integrations_needed.includes(i)}
                          onChange={() => toggleArrayItem('integrations_needed', i)}
                          className="accent-primary"
                        />
                        {i}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <ToggleYesNo
                    label="Has CMS"
                    value={siteBrief.has_cms}
                    onChange={(v) => setSiteBriefField('has_cms', v)}
                  />
                  <ToggleYesNo
                    label="Has E-commerce"
                    value={siteBrief.has_ecommerce}
                    onChange={(v) => setSiteBriefField('has_ecommerce', v)}
                  />
                  <ToggleYesNo
                    label="Has Booking"
                    value={siteBrief.has_booking}
                    onChange={(v) => setSiteBriefField('has_booking', v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground/80">Target Devices</Label>
                  <div className="flex gap-4">
                    {DEVICE_OPTIONS.map((d) => (
                      <label key={d} className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={siteBrief.target_devices.includes(d)}
                          onChange={() => toggleArrayItem('target_devices', d)}
                          className="accent-primary"
                        />
                        {d}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-foreground/80">Deadline</Label>
                  <input
                    type="date"
                    value={siteBrief.deadline}
                    onChange={(e) => setSiteBriefField('deadline', e.target.value)}
                    className="rounded-md bg-black/[0.03] border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-foreground/80">Special Requirements</Label>
                  <textarea
                    value={siteBrief.special_requirements}
                    onChange={(e) => setSiteBriefField('special_requirements', e.target.value)}
                    placeholder="Accessibility needs, performance targets, specific tech stack requirements, etc."
                    rows={2}
                    className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                  />
                </div>

                <Button
                  size="sm"
                  onClick={handleSaveSiteBrief}
                  disabled={siteBriefSaving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {siteBriefSaving ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</>
                  ) : (
                    <><Save className="w-3.5 h-3.5 mr-1.5" />Save Requirements</>
                  )}
                </Button>
              </div>
            )}
          </div>

          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Globe2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
                    Copy &amp; Imagery
                    <ProviderBadge provider={content?.generated_by} />
                  </h3>
                  <p className="text-sm text-muted-foreground">Generated from your Brand Kit and Strategy &mdash; powers both the website preview and the app preview below</p>
                </div>
              </div>
              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid="website-generate-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating copy &amp; imagery... (can take a few minutes)</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />{content ? 'Regenerate' : 'Generate'}</>
                )}
              </Button>
            </div>
          </div>

          {fetching && !content ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : !content ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Globe2 className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">No website copy yet. Generate some to see the templates.</p>
            </div>
          ) : (
            <>
              {/* Web / App view toggle */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setViewMode('web')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm border transition-colors ${
                    viewMode === 'web'
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                  }`}
                  data-testid="view-mode-web"
                >
                  <Globe2 className="w-3.5 h-3.5" />
                  Website
                </button>
                <button
                  onClick={() => setViewMode('app')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm border transition-colors ${
                    viewMode === 'app'
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                  }`}
                  data-testid="view-mode-app"
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  App
                </button>
              </div>

              {viewMode === 'web' ? (
                <>
                  {/* Template switcher */}
                  <div className="flex items-center gap-2 mb-4">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setTemplateId(t.id)}
                        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                          templateId === t.id
                            ? 'bg-primary/10 text-primary border-primary/30'
                            : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                        }`}
                        data-testid={`website-template-${t.id}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* Browser-frame preview */}
                  <div className="rounded-2xl border border-border overflow-hidden shadow-sm" data-testid="website-preview">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-black/[0.04] border-b border-border">
                      <div className="flex gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
                        <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
                        <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
                      </div>
                      <div className="flex-1 mx-4 px-3 py-1 rounded-md bg-black/[0.04] text-xs text-muted-foreground text-center truncate">
                        {domain}
                      </div>
                    </div>
                    <div className="max-h-[720px] overflow-y-auto">
                      {ActiveTemplate && <ActiveTemplate brandKit={brandKit} content={content} />}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center">
                  {/* Screen switcher */}
                  <div className="flex items-center gap-2 mb-6">
                    {APP_SCREENS.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setScreenId(s.id)}
                        className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                          screenId === s.id
                            ? 'bg-primary/10 text-primary border-primary/30'
                            : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                        }`}
                        data-testid={`app-screen-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>

                  {/* Phone frame */}
                  <PhoneFrame
                    screenId={screenId}
                    onScreenChange={setScreenId}
                    brandKit={brandKit}
                    content={content}
                    primaryColor={brandKit?.colors?.primary?.hex}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebsiteShellPanel;
